import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePerspective } from '../../contexts/PerspectiveContext';
import { useFeatures } from '../../contexts/FeatureContext';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  BookOpen,
  Users,
  Target,
  FileText,
  Shield,
  Search,
  ChevronRight,
  ChevronDown,
  Briefcase,
  FolderOpen,
  Bot,
  Eye,
  MessageSquare,
  Building2,
  UserPlus,
  Lock,
  HelpCircle,
  Zap,
  ExternalLink,
  TrendingUp,
  ShieldCheck,
  Star,
  Info,
  Kanban,
  Wallet,
  Plus,
  Settings2,
  AlertCircle,
  Sparkles,
  Filter,
  Layers,
  Archive,
  Trash2,
  Link2,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

// ============================================================================
// UNIVERSAL FAQs (Shown to All Roles)
// ============================================================================
// Each item may have an optional `feature` key — if set, the FAQ is hidden
// when that feature is not enabled in the current environment. Items
// without `feature` are always shown (security, privacy, KB basics, etc.).
const UNIVERSAL_FAQS = [
  {
    id: 'fit-score-prereqs',
    category: 'Fit & Risk Scoring',
    question: 'Why does my Fit Score say PREREQUISITES INCOMPLETE?',
    answer:
      'You haven\'t supplied enough information yet for the AI to evaluate the opportunity. At minimum you need a populated Opportunity Intake (agency, NAICS, set-aside) and at least one ingested RFP/SOW document. Once both are in place the badge changes to ANALYSIS REQUIRED, and after the AI runs it becomes AI ANALYZED with a real Fit Score.',
    icon: TrendingUp,
    tags: ['fit score', 'prerequisites', 'badge', 'scoring'],
    feature: 'go_no_go',
  },
  {
    id: 'score-badge-meanings',
    category: 'Fit & Risk Scoring',
    question: 'What do the Fit Score badges mean?',
    answer:
      'Three states: (1) PREREQUISITES INCOMPLETE — intake or documents missing, no number shown. (2) ANALYSIS REQUIRED — everything is in place but the AI hasn\'t run yet; trigger it from the Go/No-Go Dashboard. (3) AI ANALYZED — full Fit Score is displayed alongside the rationale.',
    icon: ShieldCheck,
    tags: ['badge', 'analyzed', 'analysis required', 'prerequisites'],
    feature: 'go_no_go',
  },
  {
    id: 'data-privacy',
    category: 'Security & Privacy',
    question: 'Who can see my uploaded documents?',
    answer:
      'Only members of your workspace, plus any consultant or guest you have explicitly invited to a specific pursuit. Documents and pursuits are siloed per tenant — other contractors and other consultants cannot see your data, and consultants you work with see only the pursuits they have been added to.',
    icon: Shield,
    tags: ['privacy', 'security', 'documents', 'isolation'],
  },
  {
    id: 'gold-source',
    category: 'Knowledge Base',
    question: 'What is a "Gold Source" document?',
    answer:
      'Documents auto-purge after 10 days of inactivity to keep your knowledge base lean. Promoting a document to Gold Source (toggle on the document row in Document Ingestion) makes it permanent — exempt from the 10-day purge and eligible to be referenced by future pursuits as a "best of" asset.',
    icon: Star,
    tags: ['gold source', 'purge', '10 days', 'promoted'],
    feature: 'document_ingestion',
  },
  {
    id: 'auto-purge',
    category: 'Knowledge Base',
    question: 'My documents disappeared. Why?',
    answer:
      'Standard documents are removed after 10 days of inactivity to keep storage costs and AI context windows under control. Anything you mark as Gold Source is preserved indefinitely. If you need to keep a document past 10 days, promote it to Gold Source from the Document Ingestion row.',
    icon: FolderOpen,
    tags: ['purge', 'deleted', 'retention', 'gold source'],
    feature: 'document_ingestion',
  },
  // ---------- AI Credits (prepaid pay-as-you-go) ----------
  {
    id: 'ai-credits-overview',
    category: 'AI Credits & Billing',
    question: 'How does AI billing work?',
    answer:
      'Plans do not include AI usage. AI runs (Bid Pursuit Scorecard prefill, RFP Shredder, drafting, analysis) draw from a prepaid AI Credits balance held at your workspace level. The current balance is always visible as a pill at the top right of every screen; click it to top up. Trial accounts ship with $25 in starter credits.',
    icon: Wallet,
    tags: ['ai', 'credits', 'billing', 'prepaid', 'wallet'],
  },
  {
    id: 'ai-credits-topup',
    category: 'AI Credits & Billing',
    question: 'How do I top up AI Credits?',
    answer:
      'Open Workspace Settings, find the AI Credits Balance card, and pick a pack — $200, $500, or $1,000. Stripe Checkout opens; once payment completes, your balance is updated within seconds and you are redirected back to Settings. Credits never expire and are workspace-wide (every team member draws from the same pool).',
    icon: Plus,
    tags: ['top up', 'topup', 'pack', 'stripe', 'checkout', 'balance'],
  },
  {
    id: 'ai-credits-byom',
    category: 'AI Credits & Billing',
    question: 'What is BYOM (Bring Your Own Model)?',
    answer:
      'In Workspace Settings → AI Configuration you can paste your own OpenAI / Anthropic / Gemini key. AI calls then route through your key (you pay the LLM provider directly), and the platform charges only a small orchestration fee against your AI Credits balance — so your credits stretch significantly further. Toggle BYOM off any time to switch back to platform-paid AI.',
    icon: Zap,
    tags: ['byom', 'bring your own model', 'api key', 'openai', 'anthropic'],
  },
  {
    id: 'ai-credits-auto-recharge',
    category: 'AI Credits & Billing',
    question: 'Can my AI Credits top up automatically?',
    answer:
      'Yes. On the AI Credits Balance card flip the Auto-recharge toggle and pick a pack. When your balance drops below $25 we charge your saved Stripe payment method for the configured pack so AI calls never get blocked mid-flow. You can disable auto-recharge any time and it pauses immediately.',
    icon: Settings2,
    tags: ['auto-recharge', 'auto recharge', 'low balance', 'stripe'],
  },
  {
    id: 'ai-credits-exhausted',
    category: 'AI Credits & Billing',
    question: 'Why did AI Analysis fail with "credits depleted"?',
    answer:
      'Your AI Credits balance hit $0 mid-request. The toast contains a Top Up button that takes you straight to the AI Credits Balance card; pick a pack and AI calls resume immediately. If you have BYOM configured, the depletion still applies — the small orchestration fee comes from credits, not your provider key.',
    icon: AlertCircle,
    tags: ['credits', 'depleted', 'exhausted', '402', 'top up'],
  },
  // ---------- Invitation Visibility (Apr 2026) ----------
  {
    id: 'origin-badge',
    category: 'Collaboration',
    question: 'What does the violet "Invited by client" / "Set up by consultant" badge mean?',
    answer:
      'Every pursuit card and workspace header shows this badge when the pursuit was created by an org other than your own. Consultants see "Invited by client" on pursuits owned by their linked clients; contractors see "Set up by consultant" on pursuits a consultancy created on their behalf. Pursuits you (or someone in your own org) created have no badge — they are self-created.',
    icon: Sparkles,
    tags: ['origin', 'badge', 'invited', 'collaboration', 'consultant', 'client'],
  },
  {
    id: 'origin-filter',
    category: 'Collaboration',
    question: 'How do I filter my pipeline to just invited or self-created pursuits?',
    answer:
      'On the Pursuit Pipeline toolbar, the third dropdown (next to Stage) is Origin. Choose All / Invited by client (or Set up by consultant) / Self-created. The counts in parentheses are live, so you can see at a glance how much of your pipeline is each kind.',
    icon: Filter,
    tags: ['origin', 'filter', 'pipeline', 'invited', 'self-created'],
  },
  {
    id: 'invited-no-slot',
    category: 'Collaboration',
    question: 'Do invited pursuits count toward my plan\'s pursuit limit?',
    answer:
      'No. Pursuit slots are owner-scoped — only pursuits owned by your own org count toward your plan limit. Pursuits where you were invited as a consultancy (or where a consultant set one up under your client workspace) do not consume any slot from your plan.',
    icon: Users,
    tags: ['slot', 'limit', 'invited', 'plan', 'capacity'],
  },
  // ---------- Pursuit Slots / Archive / Delete ----------
  {
    id: 'pursuit-slots',
    category: 'Pursuit Slots',
    question: 'How do pursuit slots work?',
    answer:
      'Each plan includes a base allocation of active pursuit slots (Trial 2, Growth 10, Agency 10, Enterprise unlimited). A pursuit consumes a slot the moment you create it. You can buy additional slots in packs from Workspace Settings → Pursuit Slots — purchased slots stack on top of your plan allocation and never expire.',
    icon: Layers,
    tags: ['slot', 'limit', 'capacity', 'plan', 'pursuit'],
  },
  {
    id: 'archive-pursuit',
    category: 'Pursuit Slots',
    question: 'When should I archive a pursuit?',
    answer:
      'Archive a pursuit once it reaches a terminal state — Won, Lost, No-Bid, or Cancelled. Archiving frees the slot for a new pursuit while keeping the record fully searchable for reporting. Archive from the Pursuit Pipeline (Kanban card hover) or the Opportunity Intake 3-dot menu. You can unarchive any time, provided you have a free slot.',
    icon: Archive,
    tags: ['archive', 'won', 'lost', 'no-bid', 'cancelled', 'slot'],
  },
  {
    id: 'delete-vs-archive',
    category: 'Pursuit Slots',
    question: 'What\'s the difference between Archive and Delete?',
    answer:
      'Archive (recommended) frees the slot but keeps the pursuit fully readable for reporting and past-performance write-ups. Delete is reserved for pursuits that should not have existed — duplicates, accidental imports, internal pivots. Deleted pursuits are soft-deleted with a 30-day retention window before permanent purge. Reviewers cannot delete; only Super Admin / Consultant / Contractor Admin / Bid Manager can.',
    icon: Trash2,
    tags: ['delete', 'archive', 'soft delete', 'retention', 'cancelled'],
  },
  // ─── v3.12 — Source context on extracted requirements ──────────
  {
    id: 'source-context-clickable',
    category: 'AI Features',
    question: 'Can I see where a requirement came from in the RFP?',
    answer:
      'Yes. Every requirement row extracted by the AI Shredder carries the source document name, the surrounding snippet, and the approximate page. Click any row in Compliance Tracker / Opportunity Intake to open a preview that shows exactly where in the RFP it was lifted from. Rows created before April 2026 may show "Source context unavailable" — those legacy rows weren\'t backfilled.',
    icon: Eye,
    tags: ['source', 'context', 'requirement', 'rfp', 'snippet'],
    feature: 'document_ingestion',
  },
  // ─── v3.13 — TCV Allocation ─────────────────────────────────────
  {
    id: 'tcv-allocation',
    category: 'Pursuit Workflow',
    question: 'What is the TCV Allocation card on the Pursuit Pipeline?',
    answer:
      'Total Contract Value Allocation shows how you plan to split the contract across your prime, teaming partners, and subs. If any single teaming partner ends up holding more than 50% of TCV, the partner pill turns red — a fast signal that the opportunity is over-reliant on one collaborator. Adjust splits on the pursuit\'s Teaming page.',
    icon: Building2,
    tags: ['tcv', 'allocation', 'teaming', 'partner', 'pipeline'],
  },
  // ─── v3.14 — Capability Statement generator ────────────────────
  {
    id: 'capability-statement-generator',
    category: 'AI Features',
    question: 'How do I draft a Capability Statement for a Sources Sought?',
    answer:
      'Open the pursuit and look for the "Capability Statement" page in the left menu. It is available only when the pursuit\'s procurement phase is Sources Sought or Special Notice (FAR 5.204 / 5.205). Click "Generate" — the AI drafts a federal-style capability statement tailored to the agency\'s notice, then you can edit each section inline before exporting as DOCX. Re-runs are billed against the pursuit\'s billing organization.',
    icon: Sparkles,
    tags: ['capability statement', 'sources sought', 'rfi', 'docx', 'far'],
    feature: 'go_no_go',
  },
  // ─── v3.14 — Questions for Agency generator ────────────────────
  {
    id: 'questions-for-agency',
    category: 'AI Features',
    question: 'Can the platform draft questions to submit to the Contracting Officer?',
    answer:
      'Yes. The "Questions for Agency" page on each pursuit lets the AI draft a list of clarifying questions for the CO based on the RFP/SOW. Edit them inline, then click "Submit to CO" to lock the version — the locked submission lives in the History accordion so you can re-export the DOCX any time. Each round of regeneration is billed against the pursuit\'s billing org (not the consultant\'s).',
    icon: MessageSquare,
    tags: ['questions', 'contracting officer', 'co', 'rfi', 'agency'],
    feature: 'go_no_go',
  },
  // ─── v3.15 — Ask the Library chatbot ───────────────────────────
  {
    id: 'ask-the-library',
    category: 'AI Features',
    question: 'What is "Ask the Library"?',
    answer:
      'A right-side chatbot drawer on the Knowledge Base page that lets you ask natural-language questions across your entire library — Past Performance, Key Personnel, Certifications, Promoted Pursuits, and Winning Proposals. Answers are grounded in your library only (no hallucination) and every fact is cited back to the source asset with a clickable chip. Limited to 30 questions per hour per user. Billed against the consultant wallet (or the contractor\'s own wallet for contractor-admin users).',
    icon: Sparkles,
    tags: ['ask', 'library', 'chatbot', 'kb', 'search', 'knowledge base'],
    feature: 'knowledge_base',
  },
  // ─── v3.15 — Prior Proposals matcher ───────────────────────────
  {
    id: 'prior-proposals-matcher',
    category: 'AI Features',
    question: 'Why does Go/No-Go show "Prior Proposals — Similar Evaluation Criteria"?',
    answer:
      'New auto-feature in v3.15. Once the AI Shredder identifies the pursuit\'s Section M (evaluation factors, weights, method), the system scores every winning proposal in your library against the current pursuit and surfaces the top matches. Cross-client matches are honored unless a winning proposal is flagged NDA-restricted. The threshold slider on the panel lets you raise or lower the match floor per pursuit (default: 40).',
    icon: Target,
    tags: ['prior proposals', 'section m', 'evaluation criteria', 'matcher', 'reuse'],
    feature: 'go_no_go',
  },
  // ─── v3.16.1 — Phase-aware empty state ─────────────────────────
  {
    id: 'prior-proposals-phase-not-applicable',
    category: 'AI Features',
    question: 'Why does Prior Proposals show "Evaluation criteria don\'t apply at this phase"?',
    answer:
      'Federal procurement convention: RFI / Sources Sought / Special Notice / Market Research / Pre-Solicitation notices do not carry a Section M because the government is doing market research, not source selection. The matcher activates automatically once you advance the pursuit to a solicitation phase. In the meantime, the Cross-Tool Coverage Map can surface similar past responses by NAICS and agency.',
    icon: Info,
    tags: ['prior proposals', 'phase', 'sources sought', 'rfi', 'evaluation criteria'],
    feature: 'go_no_go',
  },
  // ─── v3.16 — Pipeline Export ────────────────────────────────────
  {
    id: 'pipeline-export',
    category: 'Pursuit Workflow',
    question: 'How do I export the pursuit pipeline?',
    answer:
      'On the Master Pursuit Pipeline page, click the "Export" dropdown next to the filters and pick PDF, XLSX, or CSV. The download honors your currently-applied filters and produces an executive-grade report: PDF (landscape branded table with filter banner), XLSX (frozen header + Filters &amp; Audit sheet), CSV (UTF-8 BOM so Excel opens it cleanly). Hard cap of 25,000 rows per export. Every download is logged — open "Recent exports…" to see your history.',
    icon: FileText,
    tags: ['export', 'pdf', 'xlsx', 'csv', 'pipeline', 'report'],
  },
  {
    id: 'pipeline-export-audit',
    category: 'Pursuit Workflow',
    question: 'Who can see who downloaded the pipeline?',
    answer:
      'Every user can see their own export history. Consultants additionally see exports by users in their consultancy. Super Admins see every export across the platform. Open the "Recent exports…" item inside the Export menu to view the audit log scoped to your role.',
    icon: Shield,
    tags: ['export', 'audit', 'history', 'pipeline'],
  },
];

// ============================================================================
// ROLE-SPECIFIC FAQs
// ============================================================================
const CONTRACTOR_ADMIN_FAQS = [
  {
    id: 'invite-team',
    category: 'Team Management',
    question: 'How do I add Bid Managers or Reviewers to my workspace?',
    answer:
      'Open Workspace Settings from the sidebar, go to the Team / Members section, and send an invite. Bid Managers can create and edit pursuits; Reviewers have read-only access plus comments. Both are scoped to your workspace.',
    icon: UserPlus,
    tags: ['invite', 'team', 'bid manager', 'reviewer', 'settings'],
  },
  {
    id: 'invite-consultant',
    category: 'Team Management',
    question: 'How do I bring in an outside consultant?',
    answer:
      'Open Workspace Settings → Linked Consultancies and send an invite by email. The consultancy is linked at the workspace level — not per-pursuit — so they will see every pursuit in your workspace once they accept. Each pursuit card shows a "Set up by consultant" badge and an avatar of the consultancy that has access, so you can always see who is collaborating with you. Per-pursuit (confidential) invitation is on the v4.x roadmap.',
    icon: Briefcase,
    tags: ['consultant', 'invite', 'guest', 'pursuit', 'linked consultancy'],
  },
  {
    id: 'pursuit-lifecycle',
    category: 'Pursuit Workflow',
    question: 'What is the recommended pursuit workflow?',
    answer:
      'Opportunity Intake → Document Ingestion → Go/No-Go Dashboard → Gap Analysis & Teaming → Compliance Tracker → Proposal Workspace → Submission Guide. Each step is a left-side menu item that appears once you open a pursuit. You don\'t have to fill them in order, but earlier steps populate the AI context for later ones.',
    icon: Kanban,
    tags: ['workflow', 'lifecycle', 'menu', 'pursuit'],
    feature: 'go_no_go',
  },
  // ─── v3.14.4 — AI billing for cross-org consultants ─────────────
  {
    id: 'who-pays-for-ai',
    category: 'AI Credits & Billing',
    question: 'When a consultant works on my pursuit, whose AI credits get used?',
    answer:
      'Yours. Every pursuit carries a `billing_org_id` (set when the pursuit is created). AI calls on that pursuit — Shredder, Capability Statement, Questions for Agency, RTM, Scorecard, Prior-Proposals extraction — debit the *pursuit\'s* billing organization, not the caller\'s. So when a consultant runs an AI feature on a pursuit you own, your wallet pays and your BYOM tenant key (if any) is used. This was generalized in v3.14.4 across every pursuit-scoped AI surface.',
    icon: Wallet,
    tags: ['ai credits', 'billing', 'wallet', 'consultant', 'byom'],
  },
  // ─── v3.15 — Prior Proposals NDA gating ─────────────────────────
  {
    id: 'nda-on-winning-proposals',
    category: 'Knowledge Base',
    question: 'How do I keep a sensitive winning proposal out of cross-client matches?',
    answer:
      'When you upload or edit a Winning Proposal under Manage Documents, flip the NDA toggle on the eval-criteria pane. You can either mark the proposal NDA-restricted globally (only visible on pursuits owned by the same source org), or list specific org IDs to exclude from cross-client matching. The Prior-Proposals matcher on Go/No-Go honors these flags before showing matches.',
    icon: Lock,
    tags: ['nda', 'winning proposal', 'matcher', 'cross-client', 'privacy'],
  },
];

const BID_MANAGER_FAQS = [
  {
    id: 'create-pursuit',
    category: 'Pursuit Workflow',
    question: 'How do I create a new pursuit?',
    answer:
      'From the Pursuit Pipeline, click "New Pursuit" in the top-right. Fill in opportunity name, agency, and any optional fields you have. Once created, the pursuit-scoped left menu (Opportunity Intake → Submission Guide) appears.',
    icon: Target,
    tags: ['create', 'new pursuit', 'pipeline'],
  },
  {
    id: 'rfp-shredder',
    category: 'AI Features',
    question: 'How do I extract requirements from an RFP?',
    answer:
      'Drop the RFP into Document Ingestion. The AI Shredder runs automatically once the file is processed and populates Compliance Tracker / Gap Analysis with the extracted requirements. You can then review, edit, or add missing items by hand.',
    icon: Bot,
    tags: ['shredder', 'rfp', 'requirements', 'ingestion'],
    feature: 'document_ingestion',
  },
  {
    id: 'fit-score-improve',
    category: 'Fit & Risk Scoring',
    question: 'How do I improve a Fit Score?',
    answer:
      'Two reliable levers: (1) make sure your workspace has enough Gold Source past performance and capability statements relevant to the agency/NAICS, and (2) re-ingest the latest RFP/SOW so the AI is reasoning over the freshest scope. The Go/No-Go Dashboard shows the AI\'s reasoning trace so you can see exactly which factors moved the score.',
    icon: TrendingUp,
    tags: ['fit score', 'improve', 'pwin', 'past performance'],
    feature: 'go_no_go',
  },
];

const REVIEWER_FAQS = [
  {
    id: 'reviewer-scope',
    category: 'Access & Permissions',
    question: 'Why don\'t I see all the pursuits my organization has?',
    answer:
      'Reviewers only see pursuits they\'ve been explicitly added to. The sidebar lists those as "Assigned Pursuits". If you\'re missing one, ask the Contractor Admin or Bid Manager to add you to that pursuit.',
    icon: Lock,
    tags: ['reviewer', 'access', 'assigned'],
  },
  {
    id: 'reviewer-readonly',
    category: 'Access & Permissions',
    question: 'Can I edit proposal content?',
    answer:
      'No — Reviewers are read-only by design so the audit trail stays clean. You can read every section, leave comments, and submit Go/No-Go scoring when invited, but you can\'t modify proposal text or upload documents.',
    icon: Eye,
    tags: ['reviewer', 'read-only', 'edit'],
  },
];

const CONSULTANT_FAQS = [
  {
    id: 'consultant-isolation',
    category: 'Security & Privacy',
    question: 'Can my clients see each other\'s data?',
    answer:
      'No. Each client is a separate tenant. When you switch into Client A\'s workspace from your Client Portfolio, you only see Client A\'s pursuits and documents. Client B\'s data is invisible from that context.',
    icon: Shield,
    tags: ['privacy', 'isolation', 'clients'],
  },
  {
    id: 'consultant-kb',
    category: 'Knowledge Base',
    question: 'Is my consultancy\'s knowledge base visible to clients?',
    answer:
      'No. Anything you upload at the consultancy level is private to your firm. Clients never see your templates, methodologies, or internal documents — only the deliverables you produce inside their pursuit.',
    icon: BookOpen,
    tags: ['knowledge base', 'private', 'methodology'],
  },
  {
    id: 'consultant-switching',
    category: 'Client Management',
    question: 'How do I move between clients?',
    answer:
      'The Client Portfolio (sidebar) is your home view across all clients. Click any pursuit card to drop into that client\'s workspace; the URL changes to that client\'s slug and the rest of the app scopes to them. Use "Master Pursuit Pipeline" to see every active pursuit across all clients in one place.',
    icon: Building2,
    tags: ['switch', 'clients', 'portfolio', 'pipeline'],
  },
  {
    id: 'consultant-add-client',
    category: 'Client Management',
    question: 'How do I get connected to a contractor client?',
    answer:
      'Two paths: (1) the Contractor Admin links your consultancy from their Workspace Settings → Linked Consultancies — your firm receives an email and the client appears in your Client Portfolio once you accept. (2) From your side, open Client Directory and send the contractor an invite; once they accept, the client appears in your portfolio. Either way, every active link surfaces as a row in `consultant_client_links` and gives your consultancy visibility into all of that client\u2019s pursuits.',
    icon: UserPlus,
    tags: ['client', 'directory', 'pursuit', 'create', 'invite', 'linked'],
  },
  // ─── v3.14.4 — Billing on contractor pursuits ───────────────────
  {
    id: 'consultant-pursuit-billing',
    category: 'AI Credits & Billing',
    question: 'When I run AI on a contractor\'s pursuit, whose credits pay?',
    answer:
      'The contractor\'s. Every pursuit has a `billing_org_id` set at creation. Any pursuit-scoped AI feature you run — Shredder, Capability Statement, Questions for Agency, RTM, Scorecard, Prior-Proposals extraction — debits that pursuit\'s billing organization, not your consultancy wallet. The pursuit also uses the contractor\'s BYOM tenant key if they have one configured. Library-level features that are not tied to a pursuit (Ask the Library chatbot, winning-proposal extraction in your KB) bill your consultancy wallet instead.',
    icon: Wallet,
    tags: ['ai credits', 'billing', 'wallet', 'contractor', 'byom'],
  },
  // ─── v3.15 — Ask the Library scope ──────────────────────────────
  {
    id: 'ask-the-library-scope',
    category: 'Knowledge Base',
    question: 'Does "Ask the Library" search across all my clients?',
    answer:
      'By default, yes — across your consultancy library plus every active-linked client. The drawer has a "Client" filter chip so you can scope a single conversation to just one client when needed. NDA-restricted winning proposals are still excluded from cross-client retrieval. Every chat turn debits your consultancy wallet (this is a library-level feature, not pursuit-scoped).',
    icon: Search,
    tags: ['ask', 'library', 'scope', 'clients', 'kb', 'chat'],
    feature: 'knowledge_base',
  },
  // ─── v3.15 — Prior Proposals cross-client ───────────────────────
  {
    id: 'prior-proposals-cross-client',
    category: 'Knowledge Base',
    question: 'Does the Prior-Proposals matcher cross client boundaries?',
    answer:
      'Yes. The matcher looks at every winning proposal in your library across all linked clients to find Section M overlap with the current pursuit. Cross-client matches show a small "Cross-client" badge so the user knows the source is a sibling client. NDA-restricted proposals (via the toggle on Manage Documents) are silently excluded.',
    icon: Layers,
    tags: ['prior proposals', 'cross-client', 'matcher', 'nda', 'library'],
  },
];

// ============================================================================
// USER GUIDES (Expandable content sections)
// ============================================================================
const USER_GUIDES = {
  CONTRACTOR_ADMIN: [
    {
      title: 'Getting Started as a Contractor Admin',
      description: 'Set up your workspace, invite your team, and start your first pursuit',
      icon: Building2,
      content: [
        {
          subtitle: 'Workspace setup',
          text:
            'Open Workspace Settings from the sidebar. Fill in company details (UEI, CAGE code, NAICS codes, business size). These feed every Fit Score the AI computes.',
        },
        {
          subtitle: 'Invite your team',
          text:
            'In Workspace Settings → Team, invite Bid Managers (full create/edit rights) and Reviewers (read-only + comments). Both are workspace-scoped roles.',
        },
        {
          subtitle: 'Create your first pursuit',
          text:
            'From the Pursuit Pipeline, click "New Pursuit", then fill in the opportunity. Once created, the pursuit-scoped left menu appears for the rest of the workflow.',
        },
      ],
    },
    {
      title: 'Knowledge Base Best Practices',
      description: 'Get more accurate AI output by curating your documents',
      icon: FolderOpen,
      feature: 'document_ingestion',
      content: [
        {
          subtitle: 'Use Gold Source for your best work',
          text:
            'Promote winning past performances, key personnel resumes, and core capability statements to Gold Source. They\'re exempt from the 10-day auto-purge and become preferred references for the AI.',
        },
        {
          subtitle: 'Keep it fresh',
          text:
            'Refresh resumes, certifications, and capability docs on at least an annual cadence. Stale content drags Fit Scores down even when the underlying skills are current.',
        },
        {
          subtitle: 'Tag what you upload',
          text:
            'Document Ingestion lets you set a category for each file (RFP, Past Performance, Personnel, etc.). Accurate categories help the Shredder route content to the right place.',
        },
      ],
    },
    {
      title: 'Team & Permission Management',
      description: 'Control who sees what across your workspace and pursuits',
      icon: Users,
      content: [
        {
          subtitle: 'Workspace roles',
          text:
            'Bid Managers can create and edit any pursuit in your workspace. Reviewers are read-only with comment access on assigned pursuits. Both live in Workspace Settings → Team.',
        },
        {
          subtitle: 'External consultants',
          text:
            'Consultants are scoped per-pursuit. Invite a consultant from inside the pursuit; they only see that single pursuit in their consultant portfolio. Revoke at any time from the same place.',
        },
        {
          subtitle: 'Reviewer scope',
          text:
            'Add a Reviewer to a pursuit by opening the pursuit and using the same Invite flow. Their sidebar will show "Assigned Pursuits" with just the items you\'ve granted.',
        },
      ],
    },
    {
      title: 'Understanding the Fit Score',
      description: 'How the AI rates each opportunity for you',
      icon: TrendingUp,
      feature: 'go_no_go',
      content: [
        {
          subtitle: 'Three badge states',
          text:
            'PREREQUISITES INCOMPLETE means intake or RFP is missing. ANALYSIS REQUIRED means everything is in place but the AI hasn\'t run. AI ANALYZED means the Fit Score on screen is the live AI evaluation.',
        },
        {
          subtitle: 'What drives the score',
          text:
            'The AI cross-references the RFP against your Gold Source past performance, your NAICS profile, set-aside fit, and any teaming partners you\'ve added. The Go/No-Go Dashboard explains its reasoning so you can audit each factor.',
        },
        {
          subtitle: 'Re-running the analysis',
          text:
            'If you upload more documents or change intake fields, re-run the analysis from the Go/No-Go Dashboard to refresh the score with the new context.',
        },
      ],
    },
    {
      title: 'Exporting the Pursuit Pipeline (v3.16)',
      description: 'PDF / XLSX / CSV downloads of your filtered pipeline',
      icon: FileText,
      content: [
        {
          subtitle: 'Where to find it',
          text:
            'Open Master Pursuit Pipeline → click the Export dropdown next to the filters. Three formats: PDF (landscape branded table for board readouts), XLSX (frozen header + Filters & Audit sheet for finance), CSV (BOM-prefixed for Excel double-click).',
        },
        {
          subtitle: 'Filters carry through',
          text:
            'The export honors whatever filters are currently applied on screen (organization, stage, archived toggle). The PDF/XLSX both include a header banner listing the applied filters so the recipient knows what slice they\'re seeing.',
        },
        {
          subtitle: 'Audit log',
          text:
            'Every export writes an audit row. Use "Recent exports…" to see who downloaded what and when. Contractor Admins see their own exports; Consultants additionally see exports by their team; Super Admins see all platform-wide.',
        },
      ],
    },
    {
      title: 'AI Credits & BYOM Wallets (v3.14.4)',
      description: 'How AI usage is billed when consultants work on your pursuits',
      icon: Wallet,
      content: [
        {
          subtitle: 'Pursuit-scoped AI bills your wallet',
          text:
            'Every pursuit has a billing_org_id. Pursuit-scoped AI features (Shredder, Capability Statement, Questions for Agency, RTM, Scorecard, Prior-Proposals extraction) debit your AI Credit wallet — even when the action is triggered by a consultant working on your pursuit.',
        },
        {
          subtitle: 'BYOM tenant key respected',
          text:
            'If you\'ve configured your own BYOM (Bring Your Own Model) key, that key is used for every AI call on your pursuits — regardless of who triggers them. This keeps governance under your control and lets you negotiate your own per-token rates with OpenAI / Anthropic / Google.',
        },
        {
          subtitle: 'Library-level usage is different',
          text:
            'Features that live at the consultant library level (Ask the Library chatbot, winning-proposal eval extraction) bill the consultant\'s wallet, not yours. Only pursuit-scoped activity touches your wallet.',
        },
      ],
    },
  ],
  BID_MANAGER: [
    {
      title: 'Creating Your First Pursuit',
      description: 'From SAM.gov import to a populated workspace',
      icon: Target,
      content: [
        {
          subtitle: 'Start from the Pursuit Pipeline',
          text:
            'Click "New Pursuit". Enter the opportunity name and the agency. Optional fields (NAICS, set-aside, value, deadline, capture manager) help the AI but you can fill them later.',
        },
        {
          subtitle: 'Open Opportunity Intake first',
          text:
            'After the pursuit is created, the left menu appears. Start with Opportunity Intake to capture the basics — agency, NAICS, set-aside, deadlines.',
        },
        {
          subtitle: 'Then Document Ingestion',
          text:
            'Drop the RFP/SOW into Document Ingestion. The AI Shredder runs automatically and prepares context for any downstream features that are enabled in your environment.',
        },
      ],
    },
    {
      title: 'Working with the AI Shredder',
      description: 'How extraction works and how to validate it',
      icon: Bot,
      feature: 'document_ingestion',
      content: [
        {
          subtitle: 'Trigger',
          text:
            'The Shredder runs automatically when you ingest an RFP/SOW. There\'s also a manual "Extract Requirements" button if you want to re-run after edits.',
        },
        {
          subtitle: 'Where output lands',
          text:
            'Extracted requirements appear in Compliance Tracker (each one with a status), and gaps land in Gap Analysis & Teaming (each with a suggested partner type). Both are editable.',
        },
        {
          subtitle: 'Don\'t skip the review step',
          text:
            'AI extraction is a starting point, not a final answer. Spend 10 minutes scanning the table for missed clauses or mis-categorized items before you base a Go/No-Go on it.',
        },
      ],
    },
    {
      title: 'Proposal Workspace Guide',
      description: 'Drafting, reviewing, and finalizing sections',
      icon: FileText,
      feature: 'proposal_workspace',
      content: [
        {
          subtitle: 'Volume / Section structure',
          text:
            'The Proposal Architect (inside Proposal Workspace) organizes the RFP into volumes and sections, each linked to its source compliance items.',
        },
        {
          subtitle: 'AI-assisted drafting',
          text:
            'Each section has a Generate Draft action that pulls from your Knowledge Base. Treat the output as a first draft — accept, edit, or replace it.',
        },
        {
          subtitle: 'Track section status',
          text:
            'Section status moves NOT_STARTED → DRAFTING → IN_REVIEW → COMPLIANT. Compliance Tracker mirrors this so you can see overall pursuit readiness at a glance.',
        },
      ],
    },
  ],
  REVIEWER: [
    {
      title: 'Reviewer Quick Start',
      description: 'Find your work and give effective feedback',
      icon: Eye,
      content: [
        {
          subtitle: 'Your sidebar',
          text:
            'You\'ll see "Assigned Pursuits" — the only pursuits you have access to. The Contractor Admin or Bid Manager controls that list.',
        },
        {
          subtitle: 'What you can do',
          text:
            'Read documents, leave comments / annotations, and submit Go/No-Go scoring when invited. You can\'t edit proposal content or upload documents.',
        },
        {
          subtitle: 'Need more access?',
          text:
            'Ping the Contractor Admin or Bid Manager who invited you. They can add you to additional pursuits or upgrade your scope.',
        },
      ],
    },
    {
      title: 'Comments and Annotations',
      description: 'How to write feedback the team can act on',
      icon: MessageSquare,
      content: [
        {
          subtitle: 'Be specific',
          text:
            'Reference the requirement number when applicable so the bid team can locate it without guessing.',
        },
        {
          subtitle: 'Use the right comment type',
          text:
            'Pick Question / Suggestion / Issue / Approval. The team filters their inbox by type to triage faster.',
        },
        {
          subtitle: 'Mention people for urgent items',
          text:
            'Use @mentions in a comment to notify a specific teammate. Otherwise the comment goes into the general pursuit feed.',
        },
      ],
    },
  ],
  CONSULTANT: [
    {
      title: 'Consultant Quick Start',
      description: 'Manage multiple clients with one login',
      icon: Briefcase,
      content: [
        {
          subtitle: 'Client Portfolio is your home',
          text:
            'The sidebar opens to Client Portfolio — every client you serve, with active pursuit counts and pipeline values. Click any pursuit card to drop into that client\'s workspace.',
        },
        {
          subtitle: 'Master Pursuit Pipeline',
          text:
            'Use this when you want a single Kanban across every client at once. Filter by client, stage, or set-aside to find what you need.',
        },
        {
          subtitle: 'Your consultancy KB is private',
          text:
            'Whatever you upload at the consultancy level (templates, win themes, methodology) is invisible to clients. They only see the deliverables you produce inside their workspace.',
        },
      ],
    },
    {
      title: 'Adding and Managing Clients',
      description: 'How clients get into your portfolio',
      icon: Building2,
      content: [
        {
          subtitle: 'Client Directory',
          text:
            'Open Client Directory from the sidebar to add a contractor organization. Once added it\'s available in the New Pursuit dropdown.',
        },
        {
          subtitle: 'Pursuit creation requires a client',
          text:
            'Every pursuit must be attached to a client. If your directory is empty, the New Pursuit form shows the empty-state CTA and routes you to add one first.',
        },
        {
          subtitle: 'Switch contexts safely',
          text:
            'When you click into a client, the rest of the UI scopes to them. Documents, pursuits, and Fit Scores are theirs — your view of any other client is hidden until you switch back.',
        },
      ],
    },
    {
      title: 'Data Privacy & Isolation',
      description: 'How tenancy works for consultants',
      icon: Shield,
      content: [
        {
          subtitle: 'Per-client isolation',
          text:
            'Each client is a separate tenant. From inside Client A you cannot see Client B\'s pursuits, documents, or even user list. Switching is the only way to surface another client\'s data.',
        },
        {
          subtitle: 'Your consultancy KB',
          text:
            'Stays at your consultancy level forever. Clients never see it; they only see deliverables you publish into their pursuit.',
        },
        {
          subtitle: 'Audit trail',
          text:
            'Clients can see which consultants accessed which pursuits and when, so transparency cuts both ways.',
        },
      ],
    },
    {
      title: 'Ask the Library — Conversational KB Search (v3.15)',
      description: 'Chat with your entire consultant knowledge base',
      icon: Sparkles,
      content: [
        {
          subtitle: 'Where to find it',
          text:
            'Open Knowledge Base from the sidebar → click "Ask the Library" in the page header. A right-side chat drawer opens. Ask in plain English: "Which key personnel hold an active TS/SCI clearance?" or "List winning proposals using best-value tradeoff."',
        },
        {
          subtitle: 'Grounded answers, citation chips',
          text:
            'Answers are produced only from the assets the system retrieved from your library. Every fact is cited back to its source row via a clickable chip. If the library can\'t answer, the assistant says so — no invented numbers or names.',
        },
        {
          subtitle: 'Multi-turn, rate-limited, billed to your consultancy',
          text:
            'Conversations are multi-turn (session_id persisted across messages). 30 questions per hour per user. Every chat turn is billed against your consultancy wallet, not the contractor\'s — this is a library-level feature.',
        },
      ],
    },
    {
      title: 'Prior Proposals Matcher (v3.15)',
      description: 'Auto-surface past wins with similar Section M',
      icon: Target,
      feature: 'go_no_go',
      content: [
        {
          subtitle: 'Auto-activation',
          text:
            'Once the AI Shredder identifies a pursuit\'s Section M (evaluation factors, weights, method), the Go/No-Go panel "Prior Proposals — Similar Evaluation Criteria" scores every winning proposal in your library and shows the top matches. Cross-client matches are surfaced unless the proposal is NDA-flagged.',
        },
        {
          subtitle: 'Threshold slider',
          text:
            'Each pursuit has its own match floor (default: 40). Drag the slider on the panel to raise/lower the threshold — useful when the library is sparse and you want to see weaker matches too.',
        },
        {
          subtitle: 'Phases where it does NOT apply',
          text:
            'RFI / Sources Sought / Special Notice / Market Research / Pre-Solicitation pursuits do not have a Section M by federal convention. The panel will show an informational "Evaluation criteria don\'t apply at this phase" message in those cases — that\'s expected behavior, not a bug.',
        },
        {
          subtitle: 'NDA flags',
          text:
            'When you upload a Winning Proposal under Manage Documents, you can flag it NDA-restricted (only visible on pursuits in the same source org) or list specific excluded org IDs. The matcher honors both.',
        },
      ],
    },
  ],
  CONSULTANT_ASSOCIATE: [
    {
      title: 'Staff Consultant Quick Start',
      description: 'Working within your assigned client scope',
      icon: Briefcase,
      content: [
        {
          subtitle: 'Assigned clients only',
          text:
            'You can only see pursuits from clients your Consultant Admin has assigned you to. If a client is missing, ask for access.',
        },
        {
          subtitle: 'Full pursuit access where you have it',
          text:
            'Inside an assigned client you can create pursuits, ingest documents, and run the full workflow exactly like a Bid Manager would.',
        },
        {
          subtitle: 'Consultancy KB',
          text:
            'You can read your firm\'s knowledge base. Uploading new documents to the firm-level KB requires the Consultant Admin role.',
        },
      ],
    },
    {
      title: 'Pursuit Collaboration',
      description: 'Contributing to client pursuits effectively',
      icon: Target,
      content: [
        {
          subtitle: 'Document Ingestion',
          text:
            'You can ingest documents into your assigned clients\' pursuits. Promote your best deliverables to Gold Source so the next pursuit benefits.',
        },
        {
          subtitle: 'Go/No-Go scoring',
          text:
            'Submit your scoring on the Go/No-Go Dashboard when it\'s available in your environment. Each contributor\'s scoring shows up in the consensus view.',
        },
        {
          subtitle: 'Drafting',
          text:
            'When the Proposal Workspace is enabled, use Generate Draft actions on each section. Treat AI output as a starting point — your subject-matter knowledge is what wins.',
        },
      ],
    },
  ],
  SUPER_ADMIN: [
    {
      title: 'Platform Administration',
      description: 'Run the platform end-to-end',
      icon: Building2,
      content: [
        {
          subtitle: 'Contractor Lifecycle',
          text:
            'Track every contractor from Trial → Active → Churned. Drill into a contractor to see subscription, slot allocation, and pursuit activity.',
        },
        {
          subtitle: 'Consultant Management',
          text:
            'See every consultancy firm, the clients they serve, and aggregate activity. Use this to validate consultant–client links if support requests come in.',
        },
        {
          subtitle: 'System Docs and Roadmap',
          text:
            'System Docs holds technical documentation, data models, and API references. Product Roadmap tracks what\'s shipped and what\'s queued.',
        },
      ],
    },
    {
      title: 'Feature Releases (Production)',
      description: 'Toggle features live without redeploying',
      icon: Zap,
      content: [
        {
          subtitle: 'How toggles work',
          text:
            'On fedbd.ai, only the features you enable in the Feature Release Console are visible to end users. Toggles persist to MongoDB, propagate within ~15 seconds, and every flip is audited.',
        },
        {
          subtitle: 'Dev / Staging behavior',
          text:
            'On any non-production host (preview URLs, local), every feature is forced ON so engineers don\'t accidentally lock themselves out. Append `?features=prod` to a preview URL to simulate the production lock-down.',
        },
        {
          subtitle: 'Manual override',
          text:
            'If header-based environment detection ever fails on production, the "Pin this deployment to Production" control on the Feature Release Console writes the override directly to the database and survives any deploy.',
        },
      ],
    },
    {
      title: 'User & Access Management',
      description: 'Control platform-wide access',
      icon: Users,
      content: [
        {
          subtitle: 'User Management',
          text:
            'View every user across every workspace and consultancy. Reset passwords, change roles, deactivate accounts.',
        },
        {
          subtitle: 'Audit Logs',
          text:
            'Filter by user, action type, or date range. Use this for incident response and customer support investigations.',
        },
        {
          subtitle: 'Perspective switching',
          text:
            'From your profile you can switch into a Consultant view to QA the experience without creating a separate test account. Tenant data isolation still applies.',
        },
      ],
    },
  ],
};

// ============================================================================
// COMPONENT
// ============================================================================
export default function HelpCenter() {
  const { user } = useAuth();
  const { getEffectiveRole } = usePerspective();
  const { isFeatureEnabled, isLoaded: featuresLoaded } = useFeatures();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaqs, setExpandedFaqs] = useState({});
  const [expandedGuides, setExpandedGuides] = useState({});

  // Deep-link support:
  //   ?tab=faqs|guides    → preselect tab
  //   ?faq=<faq-id>       → expand a specific FAQ and scroll it into view
  // The Tutorials tab was removed in v3.5; legacy `?tab=tutorials` URLs fall
  // back to FAQs so old bookmarks keep working.
  const urlTab = searchParams.get('tab');
  const initialTab = urlTab === 'guides' ? 'guides' : 'faqs';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [copiedKey, setCopiedKey] = useState(null);

  // Copy a shareable URL to the clipboard. `key` drives the per-button
  // "Copied!" affordance; pass either 'page' for the header button or
  // `faq-${faq.id}` for individual FAQ share buttons. We pass a stable
  // `id` to sonner so rapid double-clicks dedupe to a single toast, and
  // we surface clipboard rejections (permission denied, insecure context)
  // as an error toast instead of an unhandled promise rejection.
  const copyShareLink = (url, key, label) => {
    const toastId = `share-${key}`;
    Promise.resolve(navigator.clipboard.writeText(url))
      .then(() => {
        setCopiedKey(key);
        toast.success(label || 'Link copied to clipboard', { id: toastId });
        setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 2000);
      })
      .catch(() => toast.error('Could not copy link', { id: toastId }));
  };

  const effectiveRole = getEffectiveRole();
  const isGuest = user?.is_guest || false;

  // Support Tickets was retired in favor of the in-pursuit "Ask this
  // Pursuit" → Feedback flow (FAB on every pursuit screen). That flow needs
  // an open pursuit, so route users to the pipeline to pick one; Super
  // Admins don't work pursuits, so send them to their dashboard instead.
  const supportPath =
    effectiveRole === 'SUPER_ADMIN'
      ? '/admin/dashboard'
      : `/${workspaceId || 'dashboard'}/pipeline`;

  // Help content respects feature flags: an FAQ / guide / tutorial that is
  // tagged with a `feature` key is hidden when that feature is disabled in
  // the current environment. This keeps users from seeing instructions for
  // capabilities they can't actually access. Items without a `feature` are
  // always shown (security, privacy, KB basics, team setup, etc.).
  // While the feature config is still loading we keep items visible to
  // avoid a content flash.
  const isItemVisible = (item) =>
    !item.feature || !featuresLoaded || isFeatureEnabled(item.feature);

  // Determine which FAQs to show based on role
  const roleFaqs = useMemo(() => {
    let faqs = [...UNIVERSAL_FAQS];
    switch (effectiveRole) {
      case 'CONTRACTOR_ADMIN':
        faqs = [...faqs, ...CONTRACTOR_ADMIN_FAQS];
        break;
      case 'BID_MANAGER':
        faqs = [...faqs, ...BID_MANAGER_FAQS];
        break;
      case 'REVIEWER':
        faqs = [...faqs, ...REVIEWER_FAQS];
        break;
      case 'CONSULTANT':
        faqs = [...faqs, ...CONSULTANT_FAQS];
        break;
      case 'CONSULTANT_ASSOCIATE':
        // Staff consultants share most concerns with consultants but don't
        // own client management workflows like adding new clients to the
        // directory — drop those FAQs to avoid sending them on a path they
        // can't complete.
        faqs = [
          ...faqs,
          ...CONSULTANT_FAQS.filter(
            (f) => f.id !== 'consultant-add-client' && f.id !== 'consultant-switching',
          ),
        ];
        break;
      default:
        break;
    }
    // Hide entries whose underlying feature is disabled in this environment.
    return faqs.filter(isItemVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveRole, featuresLoaded, isFeatureEnabled]);

  const roleGuides = useMemo(() => {
    const all = USER_GUIDES[effectiveRole] || USER_GUIDES.CONTRACTOR_ADMIN;
    return all.filter(isItemVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveRole, featuresLoaded, isFeatureEnabled]);

  // Mirror the active tab back into the URL so links stay shareable and the
  // browser back button restores the previous view.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (next.get('tab') !== activeTab) {
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // If the URL specifies an FAQ id, force the FAQs tab, expand that entry
  // and scroll it into view. Runs once after the FAQ list is materialised.
  useEffect(() => {
    const faqId = searchParams.get('faq');
    if (!faqId) return;
    setActiveTab('faqs');
    setExpandedFaqs((prev) => ({ ...prev, [faqId]: true }));
    // The card uses data-testid={`faq-${id}`}; defer to next tick so the
    // tab content has rendered before we try to scroll.
    const t = setTimeout(() => {
      const node = document.querySelector(`[data-testid="faq-${faqId}"]`);
      if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Filter FAQs based on search
  const filteredFaqs = useMemo(() => {
    if (!searchQuery.trim()) return roleFaqs;
    const query = searchQuery.toLowerCase();
    return roleFaqs.filter(
      (faq) =>
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query) ||
        faq.category.toLowerCase().includes(query) ||
        faq.tags.some((tag) => tag.includes(query)),
    );
  }, [roleFaqs, searchQuery]);

  const groupedFaqs = useMemo(() => {
    const groups = {};
    filteredFaqs.forEach((faq) => {
      if (!groups[faq.category]) groups[faq.category] = [];
      groups[faq.category].push(faq);
    });
    return groups;
  }, [filteredFaqs]);

  const toggleFaq = (faqId) => setExpandedFaqs((prev) => ({ ...prev, [faqId]: !prev[faqId] }));
  const toggleGuide = (guideIdx) => setExpandedGuides((prev) => ({ ...prev, [guideIdx]: !prev[guideIdx] }));

  const getRoleDisplayName = () => {
    switch (effectiveRole) {
      case 'CONTRACTOR_ADMIN':
        return 'Contractor Admin';
      case 'BID_MANAGER':
        return 'Bid Manager';
      case 'REVIEWER':
        return 'Reviewer';
      case 'CONSULTANT':
        return 'Consultant';
      case 'CONSULTANT_ASSOCIATE':
        return 'Staff Consultant';
      case 'SUPER_ADMIN':
        return 'Super Admin';
      default:
        return effectiveRole?.replace('_', ' ') || 'User';
    }
  };

  return (
    <div data-testid="help-center-page" className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-teal-100 rounded-xl">
              <HelpCircle className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h1
                className="text-2xl font-bold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Help & Support
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                FAQs and guides tailored for your role
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                copyShareLink(window.location.href, 'page', 'Help page link copied')
              }
              className="border-slate-200 text-slate-700"
              data-testid="copy-help-page-link-btn"
              title="Copy a deep link to this exact tab"
            >
              {copiedKey === 'page' ? (
                <Check className="w-4 h-4 mr-2 text-emerald-600" />
              ) : (
                <Link2 className="w-4 h-4 mr-2" />
              )}
              {copiedKey === 'page' ? 'Copied!' : 'Copy link'}
            </Button>
            <Badge
              className={cn(
                'text-sm px-3 py-1',
                effectiveRole === 'CONTRACTOR_ADMIN' && 'bg-teal-100 text-teal-700',
                effectiveRole === 'BID_MANAGER' && 'bg-blue-100 text-blue-700',
                effectiveRole === 'REVIEWER' && 'bg-purple-100 text-purple-700',
                effectiveRole === 'CONSULTANT' && 'bg-indigo-100 text-indigo-700',
                effectiveRole === 'CONSULTANT_ASSOCIATE' && 'bg-violet-100 text-violet-700',
                effectiveRole === 'SUPER_ADMIN' && 'bg-amber-100 text-amber-700',
              )}
            >
              {getRoleDisplayName()}
            </Badge>
            {isGuest && (
              <Badge className="bg-amber-100 text-amber-700 text-sm px-3 py-1">Guest Access</Badge>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-8">
        {/* Search Bar */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Search FAQs and guides..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 py-6 text-base bg-white border-slate-200 shadow-sm"
            data-testid="help-search-input"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 shadow-sm">
            <TabsTrigger
              value="faqs"
              className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700 px-6"
              data-testid="tab-faqs"
            >
              <Zap className="w-4 h-4 mr-2" />
              Quick FAQs
            </TabsTrigger>
            <TabsTrigger
              value="guides"
              className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700 px-6"
              data-testid="tab-guides"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              User Guides
            </TabsTrigger>
          </TabsList>

          {/* FAQs Tab */}
          <TabsContent value="faqs" className="space-y-6">
            {Object.keys(groupedFaqs).length === 0 ? (
              <Card className="border-slate-200 bg-white">
                <CardContent className="p-12 text-center">
                  <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No FAQs match your search. Try different keywords.</p>
                </CardContent>
              </Card>
            ) : (
              Object.entries(groupedFaqs).map(([category, faqs]) => (
                <div key={category} className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    {category === 'Fit & Risk Scoring' && <TrendingUp className="w-4 h-4" />}
                    {category === 'Security & Privacy' && <Shield className="w-4 h-4" />}
                    {category === 'Knowledge Base' && <FolderOpen className="w-4 h-4" />}
                    {category === 'Team Management' && <Users className="w-4 h-4" />}
                    {category === 'AI Features' && <Bot className="w-4 h-4" />}
                    {category === 'Access & Permissions' && <Lock className="w-4 h-4" />}
                    {category === 'Client Management' && <Building2 className="w-4 h-4" />}
                    {category === 'Pursuit Workflow' && <Kanban className="w-4 h-4" />}
                    {category}
                  </h3>

                  <div className="space-y-2">
                    {faqs.map((faq) => {
                      const FaqIcon = faq.icon;
                      const isExpanded = expandedFaqs[faq.id];
                      return (
                        <Card
                          key={faq.id}
                          className={cn(
                            'border-slate-200 bg-white shadow-sm transition-all cursor-pointer',
                            isExpanded && 'ring-1 ring-teal-200 border-teal-200',
                          )}
                          data-testid={`faq-${faq.id}`}
                        >
                          <CardHeader
                            className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                            onClick={() => toggleFaq(faq.id)}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={cn(
                                  'p-2 rounded-lg flex-shrink-0',
                                  isExpanded ? 'bg-teal-100' : 'bg-slate-100',
                                )}
                              >
                                <FaqIcon
                                  className={cn(
                                    'w-4 h-4',
                                    isExpanded ? 'text-teal-600' : 'text-slate-500',
                                  )}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p
                                  className={cn(
                                    'font-medium text-sm',
                                    isExpanded ? 'text-teal-700' : 'text-slate-900',
                                  )}
                                >
                                  {faq.question}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const base = `${window.location.origin}${window.location.pathname}`;
                                  copyShareLink(
                                    `${base}?tab=faqs&faq=${faq.id}`,
                                    `faq-${faq.id}`,
                                    'FAQ link copied',
                                  );
                                }}
                                className="p-1.5 rounded-md text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors flex-shrink-0"
                                title="Copy a link to this FAQ"
                                data-testid={`copy-faq-link-${faq.id}`}
                              >
                                {copiedKey === `faq-${faq.id}` ? (
                                  <Check className="w-4 h-4 text-emerald-600" />
                                ) : (
                                  <Link2 className="w-4 h-4" />
                                )}
                              </button>
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-teal-500 flex-shrink-0" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                              )}
                            </div>
                          </CardHeader>

                          {isExpanded && (
                            <CardContent className="px-4 pb-4 pt-0">
                              <div className="ml-11 pl-3 border-l-2 border-teal-200">
                                <p className="text-sm text-slate-600 leading-relaxed">
                                  {faq.answer}
                                </p>
                              </div>
                            </CardContent>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))
            )}

            {/* Contextual Help Note */}
            <Card className="border-teal-200 bg-teal-50/50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-teal-800 text-sm">Need more details?</p>
                    <p className="text-sm text-teal-700 mt-1">
                      Open the User Guides tab for step-by-step walkthroughs, or contact support for
                      anything specific to your account.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Guides Tab */}
          <TabsContent value="guides" className="space-y-4">
            {roleGuides.map((guide, idx) => {
              const GuideIcon = guide.icon;
              const isExpanded = expandedGuides[idx];
              return (
                <Card
                  key={idx}
                  className={cn(
                    'border-slate-200 bg-white shadow-sm transition-all',
                    isExpanded && 'ring-1 ring-teal-200 border-teal-200',
                  )}
                  data-testid={`guide-${idx}`}
                >
                  <CardHeader
                    className="p-5 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => toggleGuide(idx)}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={cn(
                          'p-3 rounded-lg transition-colors',
                          isExpanded ? 'bg-teal-100' : 'bg-slate-100',
                        )}
                      >
                        <GuideIcon
                          className={cn(
                            'w-5 h-5 transition-colors',
                            isExpanded ? 'text-teal-600' : 'text-slate-600',
                          )}
                        />
                      </div>
                      <div className="flex-1">
                        <p
                          className={cn(
                            'font-semibold transition-colors',
                            isExpanded ? 'text-teal-700' : 'text-slate-900',
                          )}
                        >
                          {guide.title}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">{guide.description}</p>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-teal-500 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      )}
                    </div>
                  </CardHeader>

                  {isExpanded && guide.content && (
                    <CardContent className="px-5 pb-5 pt-0">
                      <div className="ml-16 space-y-4">
                        {guide.content.map((item, contentIdx) => (
                          <div
                            key={contentIdx}
                            className="p-4 bg-slate-50 rounded-lg border-l-2 border-teal-300"
                          >
                            <p className="font-medium text-slate-900 mb-1">{item.subtitle}</p>
                            <p className="text-sm text-slate-600">{item.text}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}

            {/* Link to Full System Docs - Only for Super Admin */}
            {effectiveRole === 'SUPER_ADMIN' && (
              <Card className="border-slate-200 bg-slate-50">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-5 h-5 text-slate-500" />
                      <div>
                        <p className="font-medium text-slate-900">Full Documentation</p>
                        <p className="text-sm text-slate-500">
                          Architecture, data models, and API references
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="border-slate-300"
                      onClick={() => navigate('/admin/docs')}
                      data-testid="open-system-docs-btn"
                    >
                      Open System Docs
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Contact Support Footer */}
        <Card className="border-slate-200 bg-white mt-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-slate-100 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Still need help?</p>
                  <p className="text-sm text-slate-500">
                    {effectiveRole === 'SUPER_ADMIN'
                      ? 'Support tickets have moved — reach out to your team directly'
                      : "Open a pursuit and use the \"Ask this Pursuit\" button's Feedback option"}
                  </p>
                </div>
              </div>
              <Button
                className="bg-teal-600 hover:bg-teal-700 text-white"
                onClick={() => navigate(supportPath)}
                data-testid="contact-support-btn"
              >
                {effectiveRole === 'SUPER_ADMIN' ? 'Go to Dashboard' : 'Go to Pipeline'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
